import { MutableRefObject, ReactNode } from 'react';
import type { LinkProps } from '@vds/components';
import {
    ButtonProps,
    ButtonSize,
    CheckboxProps,
    InputProps,
    RadioGroupProps,
    RadioProps,
    SelectProps,
    SwitchProps,
} from '@vds/components';
import { BodyProps } from '@vds/components/dist/main';
import { SuperRefinement } from 'zod';

import { DateFormat } from './formatDate';

export enum ValidationTypes {
    pattern = 'pattern',
    minLength = 'minLength',
    maxLength = 'maxLength',
    min = 'min',
    max = 'max',
    step = 'step',
    required = 'required',
    typeMismatch = 'typeMismatch',
    equalsTo = 'equalsTo',
    equalsToFields = 'equalsToFields',
    blackbox = 'blackbox',
}

export type FormDataType = Record<string, unknown>;

export type FormErrorType = Record<string, string>;

export type CustomVariationType = SuperRefinement<FormDataType>;

export type FormSchema = {
    fields: FormField[];
    columns: number;
    gap?: number;
    formId: string;
    formName?: string;
    dataTestId?: string;
    enableOnChangeAutoValidation?: boolean;
    hideInlineErrors?: boolean;
    assistiveSlotAsRowGap?: boolean;
};

export type ValidationRequired = {
    type: ValidationTypes.required;
    value: boolean;
    message: string;
};

export type ValidationString = {
    type:
        | ValidationTypes.pattern
        | ValidationTypes.typeMismatch
        | ValidationTypes.equalsTo
        | ValidationTypes.blackbox;
    value: string;
    message: string;
};

export type ValidationEqualsToFields = {
    type: ValidationTypes.equalsToFields;
    value: string[];
    message: string;
};

export type ValidationNumber = {
    type:
        | ValidationTypes.minLength
        | ValidationTypes.maxLength
        | ValidationTypes.min
        | ValidationTypes.max
        | ValidationTypes.step;
    value: number;
    message: string;
};

export type Validation =
    | ValidationRequired
    | ValidationString
    | ValidationNumber
    | ValidationEqualsToFields;

export type justifyContentType = 'start' | 'end' | 'center';

export interface BaseFormField {
    id: string;
    name: string;
    label: string;
    defaultValue?: string;
    required?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    tooltip?: string;
    maxLength?: number;
    'data-ci'?: string;
    // Forwarded to the DOM so recurly.js tokenization (recurly.token(form)) can scrape the
    // field value — used by payment forms for billing-address fields (e.g. country).
    'data-recurly'?: string;
    'aria-label'?: string;
    ariaLabel?: string;
    renderer?: RendererType;
    colStart?: number;
    colEnd?: number;
    colSpan?: number;
    validation?: Validation[];
    children?: ReactNode;
    placeholder?: string;
    'link-color'?: string;
    className?: string;
    inputMode?: 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search';
    autoComplete?: InputProps['autoComplete'];
}

export interface FormTextField extends BaseFormField {
    type: FieldType.TEXT | FieldType.EMAIL;
}

export interface FormFormatedDate extends BaseFormField {
    type: FieldType.TEXT;
    value: string;
    /**
     * Region-specific DOB segment order. Defaults to `MM/DD/YYYY` when unset.
     * Set per blueprint to match the locale's expected birthdate format.
     */
    dateFormat?: DateFormat;
}

export interface FormNumberField extends BaseFormField {
    type:
        | FieldType.NUMBER
        | FieldType.CARD_NUMBER
        | FieldType.CARD_MONTH
        | FieldType.CARD_YEAR
        | FieldType.CARD_CVV;
}

export interface FormDateField extends BaseFormField {
    type: FieldType.DATE;
}

export type ForgotPasswordConfig = Partial<
    Pick<
        LinkProps,
        'href' | 'target' | 'onClick' | 'variant' | 'weight' | 'size' | 'color' | 'align'
    >
> & {
    enabled: boolean;
    // Set when the page tracks the click itself, so automatic link tracking doesn't double-count it.
    preventAutoTracking?: boolean;
    dataAttributes?: Record<string, string | undefined>;
};

export interface FormPasswordField extends BaseFormField {
    type: FieldType.PASSWORD;
    forgotPassword?: ForgotPasswordConfig;
    hintText?: string;
}

export interface FormPinField extends BaseFormField {
    type: FieldType.PIN;
    length: number;
    encrypted: boolean;
    justifyContent:
        | justifyContentType
        | { mobile: justifyContentType; desktop: justifyContentType };
    children: string;
}

export interface FormCheckboxField
    extends
        Omit<BaseFormField, 'defaultValue'>,
        Pick<CheckboxProps, 'checked' | 'size' | 'variant'> {
    type: FieldType.CHECKBOX;
    wrappingLabel?: boolean;
}

export interface FormSwitchField
    extends
        Omit<BaseFormField, 'label' | 'defaultValue'>,
        Pick<SwitchProps, 'defaultChecked' | 'hideLabel' | 'offLabel' | 'onLabel'> {
    label?: string;
    type: FieldType.SWITCH;
}

export interface FormHiddenField extends Omit<BaseFormField, 'defaultValue'> {
    type: FieldType.HIDDEN;
    defaultValue?: string | boolean;
}

export interface FormRadioGroup
    extends
        Omit<BaseFormField, 'defaultValue' | 'children'>,
        Omit<RadioGroupProps, 'id' | 'children'> {
    type: FieldType.RADIO;
    variant: RadioProps['variant'];
    size: RadioProps['size'];
    children: Omit<RadioProps, 'name' | 'id'>[];
}

export interface FormSelectField extends BaseFormField, Omit<SelectProps, 'name'> {
    type: FieldType.SELECT;
    options: SelectProps['options'];
}

export interface FormButton extends BaseFormField, Pick<ButtonProps, 'size'> {
    type: FieldType.BUTTON | FieldType.SUBMIT;
    size?: ButtonSize;
    disabled: boolean;
    overrideDisabledBehavior?: boolean;
}

// Use BodyProps to ensure compatibility with the Body component
export interface FormParagraph
    extends Omit<BaseFormField, 'children'>, Omit<BodyProps, 'component' | 'id'> {
    type: FieldType.PARAGRAPH;
    children: string | React.ReactNode;
}

export type RenderableFormField =
    | FormTextField
    | FormPasswordField
    | FormButton
    | FormDateField
    | FormCheckboxField
    | FormParagraph
    | FormNumberField
    | FormSwitchField
    | FormRadioGroup
    | FormPinField
    | FormSelectField;

export type FormField = RenderableFormField | FormHiddenField;

// A null value removes the key.
type FormOverrideValues<T> = { [K in keyof T]?: T[K] | null };

export type FormSchemaOverrides = {
    schema?: FormOverrideValues<Omit<FormSchema, 'fields'>>;
    fieldDefaults?: Record<string, unknown>;
    fields?: Record<string, Record<string, unknown>>;
    insertFields?: { before: string; field: FormField }[];
};

export type FormFieldRendererProps = {
    fieldProps: RenderableFormField;
    formErrors: FormErrorType;
    handleFormErrorByName: (name: string, message: string) => void;
    updateIsFormDataValid: () => {
        success: boolean;
        errors: FormErrorType;
        newFormData: FormDataType;
    };
    isFormValid: boolean;
};

export enum FieldType {
    TEXT = 'text',
    EMAIL = 'email',
    PASSWORD = 'password',
    SUBMIT = 'submit',
    BUTTON = 'button',
    DATE = 'date',
    CHECKBOX = 'checkbox',
    SWITCH = 'switch',
    HIDDEN = 'hidden',
    PARAGRAPH = 'paragraph',
    NUMBER = 'number',
    CARD = 'card',
    RADIO = 'radio',
    SELECT = 'select',
    CARD_NUMBER = 'card_number',
    CARD_MONTH = 'card_month',
    CARD_YEAR = 'card_year',
    CARD_CVV = 'card_cvv',
    PIN = 'pin',
    FORMATED_DATE = 'formated_date',
}

export type FieldsWithValue = Exclude<
    FieldType,
    FieldType.BUTTON | FieldType.SUBMIT | FieldType.PARAGRAPH | FieldType.HIDDEN
>;

export enum RendererType {
    DEFAULT = 'vds',
    RECURLY = 'recurly',
}

export enum BlackboxFieldValidityTypes {
    empty = '',
    valid = 'blackbox-valid-internal',
    invalid = 'blackbox-invalid-internal',
}

export type FormBuilderFormRef = MutableRefObject<HTMLFormElement | null>;

export type GlobalFormMessage = {
    message: string;
    variant?: 'success' | 'critical' | 'info';
};

export type ModalFormMessage = {
    title?: string;
    /**
     * Final display string — NOT translated by the formbuilder (unlike
     * toggleBanner): modal copy usually needs interpolation, which the
     * caller does with t(key, query) before passing it in.
     */
    message: string;
    confirmLabel?: string;
    /** Defaults to closing the modal. */
    onConfirm?: () => void;
    /** Defaults to true; when false, provide onConfirm — it is the only exit. */
    dismissible?: boolean;
    'data-ci'?: string;
};

export type SchemaModifierFun = (schemaToModify: FormSchema, region: string) => FormSchema;

export type CurriedSchemaModifierFun<ModifierArgs> = (args: ModifierArgs) => SchemaModifierFun;

export enum FormAction {
    CLICK = 'click',
    BLUR = 'blur',
    CHANGE = 'change',
    FOCUS = 'focus',
    SUBMIT = 'submit',
}

export type ActionType = (typeof FormAction)[keyof typeof FormAction];

export type ValidationProps = Partial<{
    [ValidationTypes.pattern]: string;
    [ValidationTypes.minLength]: number;
    [ValidationTypes.maxLength]: number;
    [ValidationTypes.min]: number;
    [ValidationTypes.max]: number;
    [ValidationTypes.step]: number;
    [ValidationTypes.required]: boolean;
    [ValidationTypes.typeMismatch]: boolean;
    [ValidationTypes.equalsTo]: string;
    [ValidationTypes.equalsToFields]: string;
    [ValidationTypes.blackbox]: string;
}>;

export type ValueOf<T> = T[keyof T];
